import { Component, inject, OnInit, signal } from '@angular/core';
import { DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { Params } from '../../../services/utilis/add-params-service';
import { Reservation } from '../../../models/reservation';
import { ReservationStatus } from '../../../models/enums';
import { enumToOptions } from '../../../@shared/utility/enumToOptions';
import { FormControl } from '@angular/forms';
import { ReservationService } from '../../../services/reservations/reservation-service';
import { ActivatedRoute, Router } from '@angular/router';
import { distinctUntilChanged, EMPTY, map, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { CancelReservationForm } from '../../../@shared/forms/reservations/cancel-reservation-form/cancel-reservation-form';

@Component({
  selector: 'app-reservation-list',
  imports: [TableComponent, CancelReservationForm],
  templateUrl: './reservation-list.html',
  styleUrl: './reservation-list.scss'
})
export class ReservationList implements OnInit {

  params = [
    "page",
    "size",
    "search",
    'sortBy',
    'sortDirection',
    'status'
  ]

  modifiedParams: Params = {
    page: "page",
    size: "size",
    search: "search",
    sortBy: "sortBy",
    sortDirection: "sortDirection",
    status: "status"
  }

  // Define columns to show in the table
  tableColumns: DynamicColumn<Reservation>[] = [
    { header: 'ID', field: 'id', sortable: true, },
    {
      header: 'Customer ID',
      field: 'customerId',
      sortable: true,
      command: (reservation) => this.redirectToCustomerDetailsPage(reservation),
      cellClass: 'text-primary underline cursor-pointer'
    },
    { header: 'Customer', field: 'customerName', sortable: false },
    { header: 'Phone Number', field: 'customerPhoneNumber', sortable: true },
    {
      header: 'Vehicle ID',
      field: 'carId',
      sortable: true,
      command: (reservation) => this.redirectToVehicleDetailsPage(reservation),
      cellClass: 'text-primary underline cursor-pointer'
    },
    {
      header: 'Plate NO',
      field: 'carPlateNo'
      , sortable: true,
      command: (reservation) => this.redirectToVehiclesPage(reservation),
      cellClass: 'text-primary underline cursor-pointer'
    },
    { header: 'Vehicle maker', field: 'carMaker', sortable: false },
    { header: 'Vehicle model', field: 'carModel', sortable: true },
    { header: 'Remaining time', field: 'remainingSeconds', sortable: false, isDuration: true },
    { header: 'Expire at', field: 'expiresAt', sortable: true, isDateTime: true },
    { header: 'Cancelled by', field: 'cancelledBy', sortable: true },
    { header: 'Cancelled at', field: 'cancelledAt', sortable: true, isDateTime: true },
    {
      header: 'Status', field: 'status', sortable: false, isStatusBadge: true,
      
      isFilterable: true,
      filterType: 'multiselect',
      filterOptions: enumToOptions(ReservationStatus),
      badgeStyleGetter: (v) => this.reservationStatusBadge(v as string),
    },
  
    {
      field: 'actions',
      header: 'Actions',
      sortable: false,
      actions: [
        {
          icon: 'fa-solid fa-xmark',
          command: (reservation) => this.openCancelReservationDialog(reservation),
          tooltip: 'Cancel Reservation',
          color: 'text-red-500',
          visible: (reservation) => reservation.status === ReservationStatus.ACTIVE
        },
      ],
    },
  ];

  reservationsList = signal<Reservation[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;

  searchControl = new FormControl('');

  selectedReservation: Reservation

  cancelReservationDialog = false

  private reservationService = inject(ReservationService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)

  ngOnInit() {
    this.getReservationsList()
  }

  reservationStatusBadge(value: string): { background: string; color: string } {
    const map: Record<string, { background: string; color: string }> = {
      'ACTIVE':     { background: 'var(--status-available-bg, #dcfce7)', color: 'var(--status-available-text, #16a34a)' },
      'CONVERTED':  { background: 'var(--status-converted-bg, #dbeafe)', color: 'var(--status-converted-text, #1d4ed8)' },
      'CANCELLED':  { background: 'var(--status-rejected-bg, #fee2e2)', color: 'var(--status-rejected-text, #dc2626)' },
      'EXPIRED':    { background: 'var(--status-inactive-bg, #f1f5f9)', color: 'var(--status-inactive-text, #64748b)' },
    };
    return map[value] ?? { background: 'var(--status-inactive-bg, #f1f5f9)', color: 'var(--status-inactive-text, #64748b)' };
  }

  getReservationsList = () => {
    this.activatedRoute.queryParams.pipe(
      map((params) =>
        Object.fromEntries(
          Object.entries(params)
          .filter(([key]) => this.params.includes(key) || key === 'tab')
          .map(([key, value]) => [this.modifiedParams[key] || key, value])
        )
      ),

      distinctUntilChanged((previous, current) =>
        JSON.stringify(previous) === JSON.stringify(current)
      ),

      tap((queryParams) => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
      }),
      switchMap((queryParams) => {
        const params = {
          ...queryParams,
          sortBy: queryParams['sortBy'] ?? 'createdAt',
          sortDirection: queryParams['sortDirection'] ?? 'DESC',
        };
        if(!queryParams['tab'] || queryParams['tab'] === 'RESERVATION') {
          return this.reservationService.getReservationsList(params);
        }
        else return EMPTY;
      })
    )
      .subscribe((res) => {
        this.reservationsList.set(res.data.content);
        this.totalRecords.set(res.data.totalElements)
        this.first = ((res.data.currentPage + 1) * res.data.pageSize) - res.data.pageSize
      });
  }

  onPageChange = (event: PaginatorState) => {
    const queryparams = {
      page: event.page,
      size: event.rows,
    }
    this.router.navigate([], {
      queryParams: queryparams,
      queryParamsHandling: 'merge'
    })
  }

  openCancelReservationDialog = (selectedRow: Reservation) => {
    this.selectedReservation = selectedRow
    this.cancelReservationDialog = true
  }

  onReservationCanceled = () => {
    this.getReservationsList()
    this.cancelReservationDialog = false
  }

  redirectToCustomerDetailsPage = (reservation: Reservation) => {
    this.router.navigate(['/customers', reservation.customerId])
  }

  redirectToVehicleDetailsPage = (reservation: Reservation) => {
    this.router.navigate(['/vehicles', reservation.carId])
  }

  redirectToVehiclesPage = (reservation: Reservation) => {
    this.router.navigate(['/vehicles'], {
      queryParams: { search: reservation.carPlateNo },
      queryParamsHandling: ''
    });
  }
}